import type { EventPage, EventPageBlock } from "@/interfaces/event_page";

// The page as it stands this instant, built from what the organizer has typed,
// so the preview moves with the keystroke instead of waiting on a round trip.
//
// This is a preview shape, not a security boundary. Event::Page on the server is
// still the only thing that decides what gets stored and what the public page
// renders, and its answer lands a moment later and replaces the guesswork here.
// These rules only have to be close enough that the preview doesn't flash up
// content the server is about to drop.

// Markdown is rendered and sanitized server-side, so the rendered HTML always
// lags the keystroke. It is cached by the source it was made from: text the
// organizer has already typed once keeps its formatting instead of flickering
// back to plain on every edit elsewhere on the page.
export type HtmlCache = Map<string, string>;

const USABLE_IMAGE = /^(https:\/\/|\/rails\/active_storage\/)/;

function trimmed(value: string | null | undefined) {
  return (value ?? "").trim();
}

function photo(url: string | null | undefined) {
  const value = trimmed(url);
  return USABLE_IMAGE.test(value) ? value : "";
}

function draftBlock(
  block: EventPageBlock,
  html: HtmlCache,
): EventPageBlock | null {
  switch (block.type) {
    case "hero": {
      const heading = trimmed(block.heading);
      const subheading = trimmed(block.subheading);
      if (!heading && !subheading) return null;
      return { ...block, heading, subheading };
    }
    case "text": {
      const body = trimmed(block.body);
      if (!body) return null;
      return { ...block, body, html: html.get(body) };
    }
    case "image": {
      const url = photo(block.url);
      return url ? { ...block, url } : null;
    }
    case "gallery": {
      const items = block.items
        .map((item) => ({ ...item, url: photo(item.url) }))
        .filter((item) => item.url);
      return items.length ? { ...block, items } : null;
    }
    case "schedule": {
      const items = block.items.filter((item) => trimmed(item.label));
      return items.length ? { ...block, items } : null;
    }
    case "faq": {
      const items = block.items
        .filter((item) => trimmed(item.question) && trimmed(item.answer))
        .map((item) => ({
          ...item,
          answer_html: html.get(trimmed(item.answer)),
        }));
      return items.length ? { ...block, items } : null;
    }
    case "links": {
      const items = block.items.filter(
        (item) => trimmed(item.label) && trimmed(item.url),
      );
      return items.length ? { ...block, items } : null;
    }
    case "sponsors": {
      const items = block.items
        .filter((item) => trimmed(item.name))
        .map((item) => ({ ...item, logo_url: photo(item.logo_url) || null }));
      return items.length ? { ...block, items } : null;
    }
  }
}

export function draftPage(
  blocks: EventPageBlock[],
  html: HtmlCache,
  custom: { html: string; height: number; enabled: boolean },
): EventPage {
  return {
    blocks: blocks
      .map((block) => draftBlock(block, html))
      .filter((block): block is EventPageBlock => block !== null),
    custom_html: custom.enabled ? trimmed(custom.html) || null : null,
    custom_html_height: custom.height,
    custom_html_enabled: custom.enabled,
  };
}

// Harvests the server's rendered markdown out of a preview response so the next
// keystroke can reuse it.
export function cacheHtml(cache: HtmlCache, page: EventPage | null): HtmlCache {
  if (!page) return cache;

  const next = new Map(cache);
  for (const block of page.blocks) {
    if (block.type === "text" && block.html) next.set(block.body, block.html);
    if (block.type === "faq") {
      for (const item of block.items) {
        if (item.answer_html) next.set(item.answer.trim(), item.answer_html);
      }
    }
  }
  return next;
}
